import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { getPayroll, createPayroll, getEmployees, bulkUploadPayroll, downloadPayrollTemplate, downloadSelectedPayslips, downloadPayslip } from '../api';
import api from '../api';
import { formatUploadError, downloadUploadErrorsCsv } from '../utils/uploadErrors';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { DollarSign, Plus, Download, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, CheckSquare, Square, Calculator, Settings, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const PayrollManagement = ({ user, setUser }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    month: '',
    year: new Date().getFullYear(),
    basic_salary: '',
    hra: '',
    special_allowance: '',
    incentive: '',
    other_allowances: '',
    pf_deduction: '',
    esic: '',
    professional_tax: '',
    tds: '',
    other_deductions: '',
    working_days: 26,
    paid_days: 26
  });

  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Fixed Salary state
  const [fsMonth, setFsMonth] = useState(new Date().getMonth() + 1);
  const [fsYear, setFsYear] = useState(new Date().getFullYear());
  const [fsEmployees, setFsEmployees] = useState([]);
  const [fsCalcs, setFsCalcs] = useState([]);
  const [fsLoading, setFsLoading] = useState(false);
  const [fsCalculating, setFsCalculating] = useState(false);
  const [structDialog, setStructDialog] = useState(false);
  const [structEmp, setStructEmp] = useState(null);
  const [structCtc, setStructCtc] = useState(0);
  const [structComponents, setStructComponents] = useState([]);

  // Bulk structure state
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkDeptId, setBulkDeptId] = useState('');
  const [bulkDesigId, setBulkDesigId] = useState('');
  const [bulkUseEmpCtc, setBulkUseEmpCtc] = useState(true);
  const [bulkOverrideCtc, setBulkOverrideCtc] = useState(0);
  const [bulkComponents, setBulkComponents] = useState([
    { name: 'Basic Salary', type: 'earning', amount: 40, is_percentage: false, percentage_of: null },
    { name: 'DA', type: 'earning', amount: 10, is_percentage: false, percentage_of: null },
    { name: 'HRA', type: 'earning', amount: 20, is_percentage: false, percentage_of: null },
    { name: 'Special Allowance', type: 'earning', amount: 15, is_percentage: false, percentage_of: null },
    { name: 'Conveyance Allowance', type: 'earning', amount: 5, is_percentage: false, percentage_of: null },
    { name: 'Medical Allowance', type: 'earning', amount: 5, is_percentage: false, percentage_of: null },
    { name: 'Other Allowances', type: 'earning', amount: 5, is_percentage: false, percentage_of: null },
  ]);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [deptList, setDeptList] = useState([]);
  const [desigList, setDesigList] = useState([]);

  useEffect(() => {
    api.get('/departments').then(res => setDeptList(res.data || [])).catch(() => {});
    api.get('/designations').then(res => setDesigList(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payrollRes, empRes] = await Promise.all([
        getPayroll(),
        getEmployees()
      ]);
      setPayrolls(payrollRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const response = await bulkUploadPayroll(selectedFile);
      setUploadResult(response.data);
      toast.success(`Payroll upload complete: ${response.data.created} created, ${response.data.updated} updated`);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload payroll file';
      toast.error(errorMsg);
      setUploadResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadPayrollTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payroll_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetBulkUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSelectPayslip = (payslipId) => {
    setSelectedPayslips(prev => {
      if (prev.includes(payslipId)) {
        return prev.filter(id => id !== payslipId);
      }
      return [...prev, payslipId];
    });
  };

  const handleSelectAll = () => {
    if (selectedPayslips.length === payrolls.length) {
      setSelectedPayslips([]);
    } else {
      setSelectedPayslips(payrolls.map(p => p.id));
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedPayslips.length === 0) {
      toast.error('Please select at least one payslip');
      return;
    }
    
    if (selectedPayslips.length > 50) {
      toast.error('Maximum 50 payslips can be downloaded at once');
      return;
    }

    setDownloadingSelected(true);
    try {
      const response = await downloadSelectedPayslips(selectedPayslips);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'selected_payslips.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selectedPayslips.length} payslip(s) successfully`);
      setSelectedPayslips([]);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download selected payslips');
    } finally {
      setDownloadingSelected(false);
    }
  };

  const handleDownloadSingle = async (payrollId) => {
    try {
      const response = await downloadPayslip(payrollId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download payslip');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPayroll({
        ...formData,
        year: parseInt(formData.year),
        basic_salary: parseFloat(formData.basic_salary),
        hra: parseFloat(formData.hra),
        special_allowance: parseFloat(formData.special_allowance),
        incentive: parseFloat(formData.incentive),
        other_allowances: parseFloat(formData.other_allowances || 0),
        pf_deduction: parseFloat(formData.pf_deduction),
        esic: parseFloat(formData.esic || 0),
        professional_tax: parseFloat(formData.professional_tax),
        tds: parseFloat(formData.tds),
        other_deductions: parseFloat(formData.other_deductions || 0),
        working_days: parseInt(formData.working_days),
        paid_days: parseInt(formData.paid_days)
      });
      toast.success('Payroll created successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        employee_id: '',
        month: '',
        year: new Date().getFullYear(),
        basic_salary: '',
        hra: '',
        special_allowance: '',
        incentive: '',
        other_allowances: '',
        pf_deduction: '',
        esic: '',
        professional_tax: '',
        tds: '',
        other_deductions: '',
        working_days: 26,
        paid_days: 26
      });
    } catch (error) {
      toast.error('Failed to create payroll');
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.employee_id || 'N/A';
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // ===== FIXED SALARY FUNCTIONS =====
  const fetchFsEmployees = useCallback(async () => {
    try {
      const res = await api.get('/fixed-salary/employees');
      setFsEmployees(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchFsCalcs = useCallback(async () => {
    setFsLoading(true);
    try {
      const res = await api.get(`/fixed-salary/calculations?month=${fsMonth}&year=${fsYear}`);
      setFsCalcs(res.data);
    } catch { /* ignore */ }
    finally { setFsLoading(false); }
  }, [fsMonth, fsYear]);

  useEffect(() => { fetchFsEmployees(); }, [fetchFsEmployees]);
  useEffect(() => { fetchFsCalcs(); }, [fetchFsCalcs]);

  const calculateFixedSalaries = async () => {
    setFsCalculating(true);
    try {
      const res = await api.post('/fixed-salary/calculate', { month: fsMonth, year: fsYear });
      setFsCalcs(res.data.calculations);
      toast.success(`Calculated salaries for ${res.data.total} employees`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally { setFsCalculating(false); }
  };

  const openStructDialog = async (emp) => {
    setStructEmp(emp);
    try {
      const res = await api.get(`/fixed-salary/structure/${emp.id}`);
      setStructCtc(res.data.monthly_ctc || 0);
      setStructComponents(res.data.components || []);
      setStructDialog(true);
    } catch { toast.error('Failed to load structure'); }
  };

  const addComponent = () => {
    setStructComponents(prev => [...prev, { name: '', type: 'earning', amount: 0, is_percentage: false, percentage_of: null }]);
  };

  const removeComponent = (idx) => {
    setStructComponents(prev => prev.filter((_, i) => i !== idx));
  };

  const updateComponent = (idx, field, value) => {
    setStructComponents(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const saveStructure = async () => {
    if (!structEmp) return;
    const comps = structComponents.filter(c => c.name.trim());
    if (comps.length === 0) { toast.error('Add at least one component'); return; }
    try {
      await api.put(`/fixed-salary/structure/${structEmp.id}`, { monthly_ctc: structCtc, components: comps });
      toast.success('Salary structure saved');
      setStructDialog(false);
      fetchFsEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const downloadFsPayslip = async (calcId, empCode, monthName, year) => {
    try {
      const res = await api.get(`/fixed-salary/calculations/${calcId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payslip_${empCode}_${monthName}_${year}.pdf`;
      link.click();
    } catch { toast.error('Download failed'); }
  };

  const addBulkComponent = () => {
    setBulkComponents(prev => [...prev, { name: '', type: 'earning', amount: 0, is_percentage: false, percentage_of: null }]);
  };

  const removeBulkComponent = (idx) => {
    setBulkComponents(prev => prev.filter((_, i) => i !== idx));
  };

  const updateBulkComponent = (idx, field, value) => {
    setBulkComponents(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const applyBulkStructure = async () => {
    if (!bulkDeptId && !bulkDesigId) { toast.error('Select a department or designation'); return; }
    const comps = bulkComponents.filter(c => c.name.trim());
    if (comps.length === 0) { toast.error('Add at least one component'); return; }
    setBulkApplying(true);
    try {
      const res = await api.post('/fixed-salary/structure/bulk-apply', {
        department_id: bulkDeptId || null,
        designation_id: bulkDesigId || null,
        components: comps,
        use_employee_ctc: bulkUseEmpCtc,
        override_ctc: bulkUseEmpCtc ? null : bulkOverrideCtc,
      });
      toast.success(`Structure applied to ${res.data.applied} employees`);
      setBulkDialog(false);
      fetchFsEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally { setBulkApplying(false); }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="payroll-management">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Payroll Management</h1>
          <p className="text-slate-600 mt-1">Manage employee salaries and payslips</p>
        </div>

        <Tabs defaultValue="fixed-salary" className="w-full">
          <TabsList>
            <TabsTrigger value="fixed-salary" data-testid="fixed-salary-tab">Fixed Salary</TabsTrigger>
            <TabsTrigger value="regular" data-testid="regular-payroll-tab">Regular Payroll</TabsTrigger>
          </TabsList>

          {/* ===== FIXED SALARY TAB ===== */}
          <TabsContent value="fixed-salary" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={String(fsMonth)} onValueChange={v => setFsMonth(parseInt(v))}>
                <SelectTrigger className="w-36" data-testid="fs-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(fsYear)} onValueChange={v => setFsYear(parseInt(v))}>
                <SelectTrigger className="w-24" data-testid="fs-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchFsCalcs} data-testid="fs-refresh-btn">Refresh</Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setBulkDialog(true)} className="gap-1.5" data-testid="bulk-structure-btn">
                <Settings size={14} /> Bulk Setup
              </Button>
              <Button onClick={calculateFixedSalaries} disabled={fsCalculating} className="gap-1.5" data-testid="fs-calculate-btn">
                <Calculator size={14} /> {fsCalculating ? 'Calculating...' : 'Calculate Salaries'}
              </Button>
            </div>

            {fsLoading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
            ) : fsCalcs.length === 0 ? (
              <Card>
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <Calculator size={36} className="mb-3 text-slate-300" />
                  <p className="text-sm font-medium">No calculations for {MONTH_NAMES[fsMonth - 1]} {fsYear}</p>
                  <p className="text-xs mt-1">Click "Calculate Salaries" to generate based on attendance</p>
                </div>
              </Card>
            ) : (
              <div className="overflow-auto border rounded-lg">
                <table className="w-full text-sm" data-testid="fs-calcs-table">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left p-3 font-medium text-slate-700">Employee</th>
                      <th className="text-left p-3 font-medium text-slate-700">Department</th>
                      <th className="text-center p-3 font-medium text-slate-700">CTC</th>
                      <th className="text-center p-3 font-medium text-slate-700">Present</th>
                      <th className="text-center p-3 font-medium text-slate-700">Payable</th>
                      <th className="text-right p-3 font-medium text-slate-700">Gross</th>
                      <th className="text-right p-3 font-medium text-slate-700">Deductions</th>
                      <th className="text-right p-3 font-medium text-slate-700">Net Salary</th>
                      <th className="text-center p-3 font-medium text-slate-700">Status</th>
                      <th className="text-center p-3 font-medium text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fsCalcs.map((calc, idx) => (
                      <tr key={calc.id} className="border-b hover:bg-slate-50" data-testid={`fs-calc-row-${idx}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-800">{calc.employee_name}</p>
                          <p className="text-xs text-slate-400">{calc.employee_code}</p>
                        </td>
                        <td className="p-3 text-slate-600 text-sm">{calc.department}</td>
                        <td className="p-3 text-center text-sm">₹{calc.monthly_ctc?.toLocaleString('en-IN')}</td>
                        <td className="p-3 text-center text-sm">{calc.present_days}{calc.half_days > 0 ? `+${calc.half_days}½` : ''}{calc.paid_leave_days > 0 ? <span className="text-emerald-600"> +{calc.paid_leave_days}PL</span> : ''}</td>
                        <td className="p-3 text-center font-medium text-sm">{calc.payable_days}/{calc.total_days}</td>
                        <td className="p-3 text-right text-sm text-emerald-700">₹{calc.gross_salary?.toLocaleString('en-IN')}</td>
                        <td className="p-3 text-right text-sm text-red-500">₹{calc.total_deductions?.toLocaleString('en-IN')}</td>
                        <td className="p-3 text-right font-bold text-indigo-600">₹{calc.net_salary?.toLocaleString('en-IN')}</td>
                        <td className="p-3 text-center">
                          <Badge variant="secondary" className={
                            calc.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            calc.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }>{calc.status}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" title="Configure Structure" onClick={() => {
                              const emp = fsEmployees.find(e => e.id === calc.employee_id);
                              if (emp) openStructDialog(emp);
                            }} data-testid={`fs-struct-btn-${idx}`}>
                              <Settings size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Download Payslip" onClick={() => downloadFsPayslip(calc.id, calc.employee_code, MONTH_NAMES[calc.month - 1], calc.year)} data-testid={`fs-pdf-btn-${idx}`}>
                              <FileText size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-50 font-bold text-sm">
                      <td colSpan="5" className="p-3 text-right text-indigo-800">Totals:</td>
                      <td className="p-3 text-right text-emerald-700">₹{fsCalcs.reduce((s, c) => s + (c.gross_salary || 0), 0).toLocaleString('en-IN')}</td>
                      <td className="p-3 text-right text-red-500">₹{fsCalcs.reduce((s, c) => s + (c.total_deductions || 0), 0).toLocaleString('en-IN')}</td>
                      <td className="p-3 text-right text-indigo-700">₹{fsCalcs.reduce((s, c) => s + (c.net_salary || 0), 0).toLocaleString('en-IN')}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Employee structure quick setup */}
            {fsCalcs.length === 0 && fsEmployees.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Fixed Salary Employees ({fsEmployees.length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-60 overflow-y-auto">
                    {fsEmployees.slice(0, 20).map(emp => (
                      <div key={emp.id} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{emp.name}</p>
                          <p className="text-xs text-slate-400">{emp.employee_id} · {emp.department} · ₹{emp.monthly_ctc?.toLocaleString('en-IN')}/mo</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openStructDialog(emp)} className="gap-1 text-xs" data-testid={`setup-struct-${emp.employee_id}`}>
                          <Settings size={12} /> {emp.has_structure ? 'Edit Structure' : 'Setup Structure'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== REGULAR PAYROLL TAB ===== */}
          <TabsContent value="regular" className="mt-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-3">
            {/* Bulk Upload Button */}
            <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
              setBulkUploadOpen(open);
              if (!open) resetBulkUpload();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-full" data-testid="bulk-upload-btn">
                  <Upload className="mr-2" size={20} />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="text-emerald-600" size={24} />
                    Bulk Payroll Upload
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Template Download */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600 mb-3">
                      Download the Excel template, fill in the payroll data, and upload it back.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadTemplate}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid="download-template-btn"
                    >
                      <Download className="mr-2" size={16} />
                      Download Template
                    </Button>
                  </div>

                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xlsx,.xls"
                      className="hidden"
                      id="payroll-file-upload"
                      data-testid="file-input"
                    />
                    <label htmlFor="payroll-file-upload" className="cursor-pointer">
                      <Upload className="mx-auto text-slate-400 mb-2" size={32} />
                      <p className="text-sm text-slate-600">
                        {selectedFile ? (
                          <span className="text-emerald-600 font-medium">{selectedFile.name}</span>
                        ) : (
                          <>Click to select or drag Excel file here</>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Supports .xlsx and .xls files</p>
                    </label>
                  </div>

                  {/* Upload Button */}
                  <Button
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || uploading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={18} />
                        Upload Payroll Data
                      </>
                    )}
                  </Button>

                  {/* Upload Result */}
                  {uploadResult && !uploadResult.error && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="upload-result">
                      <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                        <CheckCircle size={20} />
                        Upload Successful
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-white rounded p-2">
                          <p className="text-slate-500">Created</p>
                          <p className="text-lg font-bold text-emerald-600">{uploadResult.created}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-slate-500">Updated</p>
                          <p className="text-lg font-bold text-indigo-600">{uploadResult.updated}</p>
                        </div>
                      </div>
                      {uploadResult.errors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-1 text-amber-600 text-sm">
                              <AlertCircle size={14} />
                              {uploadResult.errors.length} row(s) had issues:
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => downloadUploadErrorsCsv(uploadResult.errors, 'payroll-upload-errors.csv')}
                              data-testid="download-upload-errors-csv"
                            >
                              Download errors (CSV)
                            </Button>
                          </div>
                          <ul className="text-xs text-slate-600 max-h-24 overflow-y-auto">
                            {uploadResult.errors.map((err, i) => (
                              <li key={i} className="py-0.5">{formatUploadError(err)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {uploadResult?.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="upload-error">
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle size={20} />
                        <span className="text-sm">{uploadResult.error}</span>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Generate Single Payroll Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="generate-payroll-btn">
                  <Plus className="mr-2" size={20} />
                  Generate Payroll
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="generate-payroll-dialog">
              <DialogHeader>
                <DialogTitle>Generate Payroll</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="payroll-form">
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee *</Label>
                  <Select
                    value={formData.employee_id}
                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="employee-select">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.employee_id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="month">Month *</Label>
                    <Select
                      value={formData.month}
                      onValueChange={(value) => setFormData({ ...formData, month: value })}
                      required
                    >
                      <SelectTrigger data-testid="month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(month => (
                          <SelectItem key={month} value={month}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="year">Year *</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      required
                      data-testid="year-input"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <h3 className="font-semibold text-emerald-700 mb-3">Earnings (in INR)</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="basic_salary">Basic Salary *</Label>
                  <Input
                    id="basic_salary"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.basic_salary}
                    onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                    required
                    data-testid="basic-salary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hra">House Rent Allowance (HRA) *</Label>
                  <Input
                    id="hra"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.hra}
                    onChange={(e) => setFormData({ ...formData, hra: e.target.value })}
                    required
                    data-testid="hra-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special_allowance">Special Allowance *</Label>
                  <Input
                    id="special_allowance"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.special_allowance}
                    onChange={(e) => setFormData({ ...formData, special_allowance: e.target.value })}
                    required
                    data-testid="special-allowance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="incentive">Incentive *</Label>
                  <Input
                    id="incentive"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.incentive}
                    onChange={(e) => setFormData({ ...formData, incentive: e.target.value })}
                    required
                    data-testid="incentive-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="other_allowances">Other Allowances</Label>
                  <Input
                    id="other_allowances"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.other_allowances}
                    onChange={(e) => setFormData({ ...formData, other_allowances: e.target.value })}
                    data-testid="other-allowances-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <h3 className="font-semibold text-rose-700 mb-3 mt-4">Deductions (in INR)</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pf_deduction">Provident Fund (PF) *</Label>
                  <Input
                    id="pf_deduction"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.pf_deduction}
                    onChange={(e) => setFormData({ ...formData, pf_deduction: e.target.value })}
                    required
                    data-testid="pf-deduction-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="esic">ESIC / Medical Insurance *</Label>
                  <Input
                    id="esic"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.esic}
                    onChange={(e) => setFormData({ ...formData, esic: e.target.value })}
                    required
                    data-testid="esic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="professional_tax">Professional Tax (PT) *</Label>
                  <Input
                    id="professional_tax"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.professional_tax}
                    onChange={(e) => setFormData({ ...formData, professional_tax: e.target.value })}
                    required
                    data-testid="professional-tax-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tds">Tax Deducted at Source (TDS) *</Label>
                  <Input
                    id="tds"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.tds}
                    onChange={(e) => setFormData({ ...formData, tds: e.target.value })}
                    required
                    data-testid="tds-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="other_deductions">Other Deductions</Label>
                  <Input
                    id="other_deductions"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={formData.other_deductions}
                    onChange={(e) => setFormData({ ...formData, other_deductions: e.target.value })}
                    data-testid="other-deductions-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <h3 className="font-semibold text-indigo-700 mb-3 mt-4">Days Information</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="working_days">Working Days *</Label>
                  <Input
                    id="working_days"
                    type="number"
                    value={formData.working_days}
                    onChange={(e) => setFormData({ ...formData, working_days: e.target.value })}
                    required
                    data-testid="working-days-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paid_days">Paid Days *</Label>
                  <Input
                    id="paid_days"
                    type="number"
                    value={formData.paid_days}
                    onChange={(e) => setFormData({ ...formData, paid_days: e.target.value })}
                    required
                    data-testid="paid-days-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="submit-payroll-btn">
                  Generate Payroll
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Selection Bar - Shows when payrolls exist */}
        {payrolls.length > 0 && (
          <div className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-600 transition-colors"
                data-testid="select-all-btn"
              >
                {selectedPayslips.length === payrolls.length && payrolls.length > 0 ? (
                  <CheckSquare className="text-indigo-600" size={20} />
                ) : (
                  <Square size={20} />
                )}
                <span>
                  {selectedPayslips.length === payrolls.length && payrolls.length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </span>
              </button>
              {selectedPayslips.length > 0 && (
                <span className="text-sm text-indigo-600 font-medium" data-testid="selection-count">
                  {selectedPayslips.length} selected
                </span>
              )}
            </div>
            <Button
              onClick={handleDownloadSelected}
              disabled={selectedPayslips.length === 0 || downloadingSelected}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full disabled:opacity-50"
              data-testid="download-selected-btn"
            >
              {downloadingSelected ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="mr-2" size={18} />
                  Download Selected ({selectedPayslips.length})
                </>
              )}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {payrolls.length === 0 ? (
            <Card className="col-span-2 border border-slate-100 shadow-sm">
              <CardContent className="p-12 text-center">
                <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No payroll records found</p>
              </CardContent>
            </Card>
          ) : (
            payrolls.map((payroll, index) => (
              <Card 
                key={payroll.id} 
                className={`border shadow-sm card-hover transition-all ${
                  selectedPayslips.includes(payroll.id) 
                    ? 'border-indigo-400 ring-2 ring-indigo-100' 
                    : 'border-slate-100'
                }`} 
                data-testid={`payroll-card-${index}`}
              >
                <CardHeader className="border-b border-slate-50 bg-slate-50/50">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Checkbox for selection */}
                      <Checkbox
                        checked={selectedPayslips.includes(payroll.id)}
                        onCheckedChange={() => handleSelectPayslip(payroll.id)}
                        className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                        data-testid={`payroll-checkbox-${index}`}
                      />
                      <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-bold mono">
                        {getEmployeeName(payroll.employee_id).substring(0, 2)}
                      </div>
                      <div>
                        <p className="text-base" data-testid={`payroll-employee-${index}`}>{getEmployeeName(payroll.employee_id)}</p>
                        <p className="text-sm font-normal text-slate-600">{payroll.month} {payroll.year}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-indigo-600 hover:bg-indigo-50" 
                      onClick={() => handleDownloadSingle(payroll.id)}
                      data-testid={`download-btn-${index}`}
                    >
                      <Download size={18} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Gross Salary</span>
                    <span className="font-medium mono text-slate-900">₹{payroll.gross_salary?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Deductions</span>
                    <span className="font-medium mono text-rose-600">-₹{payroll.total_deductions?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="pt-3 border-t border-slate-200">
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-900">Net Salary</span>
                      <span className="text-xl font-bold mono text-indigo-600" data-testid={`net-salary-${index}`}>₹{payroll.net_salary?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
          </TabsContent>
        </Tabs>

        {/* ===== SALARY STRUCTURE DIALOG ===== */}
        <Dialog open={structDialog} onOpenChange={setStructDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Salary Structure — {structEmp?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Monthly CTC (₹)</Label>
                <Input type="number" value={structCtc} onChange={e => setStructCtc(parseFloat(e.target.value) || 0)} data-testid="struct-ctc-input" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Components ({structComponents.length})</Label>
                  <Button variant="outline" size="sm" onClick={addComponent} className="gap-1 text-xs" data-testid="add-component-btn">
                    <Plus size={12} /> Add Component
                  </Button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {structComponents.map((comp, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`component-row-${idx}`}>
                      <Input
                        placeholder="Component name"
                        value={comp.name}
                        onChange={e => updateComponent(idx, 'name', e.target.value)}
                        className="flex-1"
                      />
                      <Select value={comp.type} onValueChange={v => updateComponent(idx, 'type', v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="earning">Earning</SelectItem>
                          <SelectItem value="deduction">Deduction</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={comp.amount}
                        onChange={e => updateComponent(idx, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-28"
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeComponent(idx)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
                {structComponents.length > 0 && (
                  <div className="flex justify-between text-sm mt-2 px-2">
                    <span className="text-emerald-600">Earnings: ₹{structComponents.filter(c => c.type === 'earning').reduce((s, c) => s + c.amount, 0).toLocaleString('en-IN')}</span>
                    <span className="text-red-500">Deductions: ₹{structComponents.filter(c => c.type === 'deduction').reduce((s, c) => s + c.amount, 0).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStructDialog(false)}>Cancel</Button>
              <Button onClick={saveStructure} data-testid="save-structure-btn">Save Structure</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== BULK SALARY STRUCTURE DIALOG ===== */}
        <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings size={18} className="text-indigo-500" /> Bulk Salary Structure Setup
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Apply the same salary component template to all fixed-salary employees in a department or designation. Each employee's CTC is preserved; components are scaled proportionally.</p>

              {/* Department & Designation Selectors — filtered by actual employees */}
              {(() => {
                // Departments that have at least one fixed-salary employee
                const empDeptNames = new Set(fsEmployees.map(e => e.department).filter(Boolean));
                const availableDepts = deptList.filter(d => d.id && empDeptNames.has(d.name));
                // Selected department name for filtering designations
                const selectedDeptName = bulkDeptId ? (deptList.find(d => d.id === bulkDeptId)?.name || '') : '';
                // Designations filtered by selected department's employees
                const filteredEmps = selectedDeptName ? fsEmployees.filter(e => e.department === selectedDeptName) : fsEmployees;
                const empDesigNames = new Set(filteredEmps.map(e => e.designation).filter(Boolean));
                const availableDesigs = desigList.filter(d => d.id && empDesigNames.has(d.title));
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Department</Label>
                      <Select value={bulkDeptId || "__all__"} onValueChange={v => { setBulkDeptId(v === "__all__" ? "" : v); setBulkDesigId(''); }}>
                        <SelectTrigger data-testid="bulk-dept-select"><SelectValue placeholder="All departments" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Departments</SelectItem>
                          {availableDepts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Select value={bulkDesigId || "__all__"} onValueChange={v => setBulkDesigId(v === "__all__" ? "" : v)}>
                        <SelectTrigger data-testid="bulk-desig-select"><SelectValue placeholder="All designations" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Designations</SelectItem>
                          {availableDesigs.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })()}
              <p className="text-xs text-slate-400 -mt-2">
                {bulkDeptId && bulkDesigId ? 'Filters combined: employees matching both department AND designation' :
                 bulkDeptId ? 'All employees in selected department' :
                 bulkDesigId ? 'All employees with selected designation' :
                 'Select at least one filter'}
              </p>

              {/* CTC Mode */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Use each employee's existing CTC</Label>
                  <Switch checked={bulkUseEmpCtc} onCheckedChange={setBulkUseEmpCtc} data-testid="bulk-use-emp-ctc" />
                </div>
                {!bulkUseEmpCtc && (
                  <div>
                    <Label className="text-xs text-slate-500">Override CTC for all (₹)</Label>
                    <Input type="number" value={bulkOverrideCtc} onChange={e => setBulkOverrideCtc(parseFloat(e.target.value) || 0)} data-testid="bulk-override-ctc" />
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  {bulkUseEmpCtc
                    ? 'Components will be scaled proportionally to each employee\'s CTC'
                    : 'All employees will get the same fixed amounts'}
                </p>
              </div>

              {/* Component Template */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Component Template ({bulkComponents.length})</Label>
                  <Button variant="outline" size="sm" onClick={addBulkComponent} className="gap-1 text-xs" data-testid="bulk-add-component-btn">
                    <Plus size={12} /> Add Component
                  </Button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {bulkComponents.map((comp, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`bulk-component-${idx}`}>
                      <Input
                        placeholder="Component name"
                        value={comp.name}
                        onChange={e => updateBulkComponent(idx, 'name', e.target.value)}
                        className="flex-1"
                      />
                      <Select value={comp.type} onValueChange={v => updateBulkComponent(idx, 'type', v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="earning">Earning</SelectItem>
                          <SelectItem value="deduction">Deduction</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder={bulkUseEmpCtc ? '% ratio' : 'Amount'}
                        value={comp.amount}
                        onChange={e => updateBulkComponent(idx, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-28"
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeBulkComponent(idx)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
                {bulkUseEmpCtc && bulkComponents.length > 0 && (
                  <p className="text-xs text-indigo-500 mt-1.5">
                    Ratio: {bulkComponents.filter(c => c.type === 'earning').map(c => `${c.name} ${Math.round(c.amount / bulkComponents.filter(x => x.type === 'earning').reduce((s, x) => s + x.amount, 0) * 100)}%`).join(' · ')}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialog(false)}>Cancel</Button>
              <Button onClick={applyBulkStructure} disabled={bulkApplying || (!bulkDeptId && !bulkDesigId)} className="gap-1.5" data-testid="bulk-apply-btn">
                <CheckCircle size={14} /> {bulkApplying ? 'Applying...' : 'Apply Structure'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PayrollManagement;